import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  previewContract,
  createContract,
  previewContractBundle,
  Position,
  PreviewResponse,
  ContractCreationPayload,
  AttachmentPayload,
} from '../services/ContractWizardService';
import { listTemplates, Template, seedTemplates } from '../../templates/services/TemplatesService';
import { AttachmentTemplate, AttachmentSelection } from '../services/AttachmentService';
import { useCompensationUnlock } from '../../employees/hooks/useCompensationUnlock';
import { toast } from 'react-toastify';
import { absolutizePdfUrl } from '../../../utils/pdfUtils';
import { serializeConditionalValues, ConditionalFieldPresence } from '../utils/conditionals';
import {
  STEP1_KEYS,
  norm,
  findTemplate,
  toUppercaseCompType,
  calculateProbationEndDate,
  weeklyToMonthly,
  monthlyToWeekly,
  validateRequiredFields,
  validateAttachmentFields,
  resolvePositionTitles,
} from './useContractWizard';

interface HandlerDeps {
  employeeId: number;
  selectedWA: number | null;
  selectedEmploymentType: string;
  selectedPositionKey: string;
  startDate: string;
  compensationType: 'hourly' | 'salary';
  formValues: Record<string, any>;
  positions: Position[];
  presence: ConditionalFieldPresence;
  allTemplates: Template[];
  selectedTemplateId: string;
  attachments: AttachmentTemplate[];
  attachmentSelections: AttachmentSelection[];
  blockValues: Record<string, string>;
  initialBlockValues: Record<string, string>;
  patchProfile: boolean;
  contractEndDate: string;
  probationMonths: number | null;
  extraLeaveDays: number | null;
  makeCoverPage: boolean;
  makeCombinedPdf: boolean;
  preview: PreviewResponse | null;

  setAllTemplates: (t: Template[]) => void;
  setLoadingTemplates: (v: boolean) => void;
  setLoadingPreview: (v: boolean) => void;
  setLoadingPdfPreview: (v: boolean) => void;
  setPreview: (p: PreviewResponse | null) => void;
  setPreviewSrc: (src: string | Blob | undefined) => void;
  setSelectedTemplateId: (id: string) => void;
  setSelectedSuggestionIdx: (idx: number) => void;
  setError: (err: string) => void;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  setBlockValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setInitialBlockValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setProbationMonths: (v: number | null) => void;
  setExtraLeaveDays: (v: number | null) => void;
  setCompensationType: (t: 'hourly' | 'salary') => void;
  setHourlyRate: (v: number | null) => void;
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setAttachmentValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setLoading: (v: boolean) => void;
  setMergeWarnings: (warnings: string[]) => void;
  setSeeding: (v: boolean) => void;
  setActiveStep: (step: number) => void;

  onSuccess?: (data: any) => void;
  onClose: () => void;
  ensureUnlocked: () => Promise<boolean>;
  navigate: ReturnType<typeof useNavigate>;
}

export function createHandlers(deps: HandlerDeps) {
  const loadAllTemplates = async (): Promise<Template[]> => {
    deps.setLoadingTemplates(true);
    try {
      const response = await listTemplates({ active: true });
      deps.setAllTemplates(response.results);
      return response.results;
    } catch (err: any) {
      console.error('Failed to load templates:', err);
      deps.setAllTemplates([]);
      return [];
    } finally {
      deps.setLoadingTemplates(false);
    }
  };

  const handleManualTemplateSelect = async (templateKey: string) => {
    deps.setSelectedTemplateId(templateKey);
    deps.setLoadingPreview(true);
    try {
      const template = findTemplate(deps.allTemplates, templateKey);
      if (template && template.schema) {
        deps.setPreview({
          ...deps.preview!,
          schema: { required_fields: template.schema.required_fields, optional_fields: template.schema.optional_fields },
        });
        const initialValues: Record<string, any> = {};
        [...template.schema.required_fields, ...template.schema.optional_fields].forEach((field) => {
          if (STEP1_KEYS.has(field.key)) return;
          if (field.default_value !== undefined && field.default_value !== null) initialValues[field.key] = field.default_value;
        });
        deps.setFormValues(initialValues);
      }
    } catch (err: any) {
      console.error('Failed to load template schema:', err);
    } finally {
      deps.setLoadingPreview(false);
    }
  };

  const handleSeedTemplates = async () => {
    deps.setSeeding(true);
    try {
      const result = await seedTemplates({ pack: 'service_cafe_de_basic' });
      toast.success(`Seeded ${result.created_count} templates (${result.skipped_count} skipped)`);
      await handlePreview();
    } catch (err: any) {
      console.error('Failed to seed templates:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      toast.error(<div><div>Failed to seed templates</div>{traceId && <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Trace ID: {traceId}</div>}</div>);
    } finally {
      deps.setSeeding(false);
    }
  };

  const handleNavigateToLibrary = () => {
    const params = new URLSearchParams();
    if (deps.selectedWA) params.set('wa', deps.selectedWA.toString());
    if (deps.selectedEmploymentType) params.set('type', deps.selectedEmploymentType);
    if (deps.selectedPositionKey) params.set('position', deps.selectedPositionKey);
    deps.navigate(`/admin/templates?${params.toString()}`);
  };

  const handlePreviewPdf = async () => {
    if (!deps.selectedTemplateId) { toast.error('Please select a template first'); return; }

    let template = findTemplate(deps.allTemplates, deps.selectedTemplateId);
    if (!template) {
      const freshTemplates = await loadAllTemplates();
      template = findTemplate(freshTemplates, deps.selectedTemplateId);
      if (!template) { toast.error('Template not found. Please try again.'); return; }
    }

    const templateId = template.id;
    deps.setLoadingPdfPreview(true);
    try {
      const { employeePositionTitle, roleDescriptionText } = resolvePositionTitles(
        deps.selectedPositionKey === null ? null : deps.selectedPositionKey || null, deps.positions, deps.formValues
      );
      const conditionalOverrides = serializeConditionalValues(deps.formValues, deps.presence);
      const normalizedEmploymentType = norm(deps.selectedEmploymentType);

      const variables: Record<string, any> = {
        employee_id: deps.employeeId, ...deps.formValues, ...conditionalOverrides,
        start_date: deps.startDate, employment_type: normalizedEmploymentType,
        'employee.position_title': employeePositionTitle,
        ...(roleDescriptionText ? { role_description_text: roleDescriptionText } : {}),
      };
      if (deps.compensationType === 'hourly') {
        variables['hourly_rate'] = deps.formValues['hourly_rate'];
        variables['monthly_hours'] = deps.formValues['monthly_hours'];
      } else {
        variables['salary_monthly_cents'] = deps.formValues['salary_monthly_cents'];
        variables['monthly_hours'] = deps.formValues['monthly_hours'];
      }

      const attachmentsPayload = deps.attachmentSelections.map((sel) => {
        const att = deps.attachments.find((a) => a.template_id === sel.template_id);
        return { template_id: att?.template_key || String(sel.template_id), variables: sel.variables || {} };
      });

      const res = await previewContractBundle(deps.employeeId, { template_id: templateId, variables, attachments: attachmentsPayload });

      if (res.pdf_bytes_b64) {
        const bin = atob(res.pdf_bytes_b64);
        const buf = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
        deps.setPreviewSrc(new Blob([buf], { type: 'application/pdf' }));
        toast.success(`PDF Vorschau generiert (${attachmentsPayload.length} Annexe)`);
        return;
      }
      if (res.pdf_url) {
        const absUrl = absolutizePdfUrl(res.pdf_url);
        if (absUrl) {
          try {
            const response = await fetch(absUrl);
            if (!response.ok) throw new Error('Failed to fetch PDF');
            deps.setPreviewSrc(await response.blob());
            toast.success(`PDF Vorschau generiert (${attachmentsPayload.length} Annexe)`);
            return;
          } catch (fetchError) {
            console.error('Failed to fetch PDF:', fetchError);
            toast.error('Failed to load PDF preview');
            deps.setPreviewSrc(undefined);
            return;
          }
        }
      }
      if (res.combined_html) {
        const wrappedHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body{margin:0;padding:20px;font-family:Arial,sans-serif;background:white;overflow-x:auto}*{box-sizing:border-box}</style></head><body>${res.combined_html}</body></html>`;
        deps.setPreviewSrc(new Blob([wrappedHtml], { type: 'text/html' }));
        toast.success(`Vorschau generiert (${attachmentsPayload.length} Annexe)`);
        return;
      }
      toast.error('Preview fehlgeschlagen: keine PDF oder HTML vom Server erhalten.');
      deps.setPreviewSrc(undefined);
    } catch (err: any) {
      console.error('Failed to preview PDF:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      const errMsg = err?.response?.data?.message || err?.response?.data?.detail || err?.response?.data?.error || 'Failed to generate PDF preview';
      toast.error(<div><div>{errMsg}</div>{traceId && <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Trace ID: {traceId}</div>}</div>);
      deps.setPreviewSrc(undefined);
    } finally {
      deps.setLoadingPdfPreview(false);
    }
  };

  const handlePreview = async () => {
    deps.setLoadingPreview(true);
    deps.setError('');
    try {
      const payload: any = { employment_type: norm(deps.selectedEmploymentType), start_date: deps.startDate || undefined };
      payload.work_area_id = deps.selectedWA !== null && deps.selectedWA !== undefined ? deps.selectedWA : null;
      if (deps.selectedPositionKey === null) payload.position_key = null;
      else if (deps.selectedPositionKey) payload.position_key = deps.selectedPositionKey;

      const response = await previewContract(deps.employeeId, payload);
      const data = {
        suggestions: Array.isArray(response?.suggestions) ? response.suggestions : [],
        schema: response?.schema || null,
        defaults: response?.defaults || {},
        blocks: Array.isArray(response?.blocks) ? response.blocks : [],
      };
      if (data.schema) {
        data.schema.required_fields = Array.isArray(data.schema.required_fields) ? data.schema.required_fields : [];
        data.schema.optional_fields = Array.isArray(data.schema.optional_fields) ? data.schema.optional_fields : [];
      }
      deps.setPreview(data);

      if (data.suggestions.length > 0) { deps.setSelectedTemplateId(data.suggestions[0].template_id); deps.setSelectedSuggestionIdx(0); }
      else { await loadAllTemplates(); }

      if (data.defaults.probation_months != null) deps.setProbationMonths(data.defaults.probation_months);
      if (data.defaults.extra_leave_days_per_year != null) deps.setExtraLeaveDays(data.defaults.extra_leave_days_per_year);
      if (data.defaults.compensation_type) deps.setCompensationType(data.defaults.compensation_type);
      if (data.defaults.min_wage_hourly) deps.setHourlyRate(data.defaults.min_wage_hourly);

      if (data.schema) {
        const initialValues: Record<string, any> = {};
        [...data.schema.required_fields, ...data.schema.optional_fields].forEach((field) => {
          if (STEP1_KEYS.has(field.key)) return;
          if (field.default_value !== undefined && field.default_value !== null) initialValues[field.key] = field.default_value;
        });
        if (data.defaults && typeof data.defaults === 'object') {
          Object.entries(data.defaults).forEach(([key, value]) => { if (!STEP1_KEYS.has(key) && value != null) initialValues[key] = value; });
        }
        deps.setFormValues(initialValues);
      }

      if (data.blocks) {
        const initialBlocks: Record<string, string> = {};
        data.blocks.forEach((block) => { if (block.default_content) initialBlocks[block.block_key] = block.default_content; });
        deps.setBlockValues(initialBlocks);
        deps.setInitialBlockValues(initialBlocks);
      }
    } catch (err: any) {
      console.error('Preview Error:', err);
      const errMsg = err?.response?.data?.message || err?.response?.data?.detail || 'Failed to preview contract';
      deps.setError(JSON.stringify({ message: errMsg, status: err?.response?.status, url: err?.config?.url, requestPayload: err?.config?.data ? JSON.parse(err.config.data) : null }, null, 2));
    } finally {
      deps.setLoadingPreview(false);
    }
  };

  const handleCreate = async () => {
    const errors = validateRequiredFields(deps.preview, deps.selectedEmploymentType, deps.startDate, deps.compensationType, deps.formValues, deps.presence);
    if (Object.keys(errors).length > 0) { deps.setValidationErrors(errors); deps.setError('Please fill in all required fields'); return; }

    const attachErrors = validateAttachmentFields(deps.attachmentSelections, deps.attachments);
    if (Object.keys(attachErrors).length > 0) { deps.setAttachmentValidationErrors(attachErrors); deps.setError('Bitte füllen Sie alle erforderlichen Annex-Felder aus'); return; }

    const pnw = Number(deps.formValues['probation_notice_weeks'] ?? 2);
    const naw = Number(deps.formValues['notice_after_probation_weeks'] ?? 4);
    deps.formValues['probation_notice_weeks'] = Math.max(2, pnw);
    deps.formValues['notice_after_probation_weeks'] = Math.max(4, naw);

    deps.setLoading(true);
    deps.setError('');
    let payload: ContractCreationPayload | undefined;

    try {
      const unlocked = await deps.ensureUnlocked();
      if (!unlocked) { deps.setLoading(false); return; }

      const probationEndDate = deps.probationMonths && deps.startDate ? calculateProbationEndDate(deps.startDate, deps.probationMonths) : null;
      const changedBlocks: Record<string, string> = {};
      Object.keys(deps.blockValues).forEach((key) => { if (deps.blockValues[key] !== deps.initialBlockValues[key]) changedBlocks[key] = deps.blockValues[key]; });

      const { employeePositionTitle, roleDescriptionText, positionTitle } = resolvePositionTitles(
        deps.selectedPositionKey === null ? null : deps.selectedPositionKey || null, deps.positions, deps.formValues
      );
      const normalizedEmploymentType = (deps.selectedEmploymentType || '').toUpperCase();
      const actualCompensationType = normalizedEmploymentType === 'MINIJOB' ? 'hourly' : deps.compensationType;

      const conditionalOverrides = serializeConditionalValues(deps.formValues, deps.presence);
      const overrides: Record<string, any> = {
        ...deps.formValues, ...conditionalOverrides, start_date: deps.startDate,
        'employee.position_title': employeePositionTitle,
        ...(roleDescriptionText ? { role_description_text: roleDescriptionText } : {}),
      };
      if (deps.compensationType === 'hourly') { overrides['hourly_rate'] = deps.formValues['hourly_rate']; overrides['monthly_hours'] = deps.formValues['monthly_hours']; }
      else { overrides['salary_monthly_cents'] = deps.formValues['salary_monthly_cents']; overrides['monthly_hours'] = deps.formValues['monthly_hours']; }

      const attachmentPayloads: AttachmentPayload[] = deps.attachmentSelections.map((sel) => {
        const att = deps.attachments.find((a) => a.template_id === sel.template_id);
        return { template_id: att?.template_key || String(sel.template_id), variables: sel.variables || {} };
      });

      payload = {
        template_id: deps.selectedTemplateId, patch_profile: deps.patchProfile,
        profile_patch: {
          primary_work_area: deps.selectedWA || undefined, employment_type: normalizedEmploymentType,
          position_title: positionTitle, start_date: deps.startDate || undefined,
          contract_end_date: deps.contractEndDate || null, probation_end_date: probationEndDate,
          holiday_extra_days_per_year: deps.extraLeaveDays,
          contract_hours_per_week: deps.formValues['contract_hours_per_week'] ? Number(deps.formValues['contract_hours_per_week']) : undefined,
        },
        compensation: {
          type: toUppercaseCompType(actualCompensationType),
          hourly_rate: actualCompensationType === 'hourly' ? String(deps.formValues['hourly_rate']) : undefined,
          monthly_salary: actualCompensationType === 'salary' ? String(deps.formValues['salary_monthly_cents']) : undefined,
          contract_hours_per_week: deps.formValues['contract_hours_per_week'] ? Number(deps.formValues['contract_hours_per_week']) : undefined,
          monthly_hours: deps.formValues['monthly_hours'] ? Number(deps.formValues['monthly_hours']) : undefined,
        },
        overrides,
        blocks: Object.keys(changedBlocks).length > 0 ? changedBlocks : undefined,
        attachments: attachmentPayloads.length > 0 ? attachmentPayloads : undefined,
        bundle: attachmentPayloads.length > 0 ? { make_cover_page: deps.makeCoverPage, make_combined_pdf: deps.makeCombinedPdf } : undefined,
        idempotency_key: crypto.randomUUID(),
      };

      const result = await createContract(deps.employeeId, payload);
      const warnings = result.bundle?.merge?.warnings || [];
      if (warnings.length > 0) {
        deps.setMergeWarnings(warnings);
        setTimeout(() => { deps.setMergeWarnings([]); deps.onSuccess?.(result as any); deps.onClose(); }, 6000);
      } else {
        deps.onSuccess?.(result as any);
        deps.onClose();
      }
      deps.setLoading(false);
    } catch (err: any) {
      console.error('Contract creation failed:', { status: err?.response?.status, data: err?.response?.data, payload });
      let errMsg = 'Failed to create contract';
      if (err?.response?.status === 422) {
        const ve = err?.response?.data?.details || err?.response?.data?.detail;
        if (ve && typeof ve === 'object' && !Array.isArray(ve)) errMsg = `Validation error: ${Object.entries(ve).map(([f, m]) => `${f}: ${m}`).join(', ')}`;
        else if (Array.isArray(ve)) errMsg = `Validation error: ${ve.map((e: any) => `${e.loc?.join('.') || 'unknown'}: ${e.msg}`).join(', ')}`;
        else if (typeof ve === 'string') errMsg = `Validation error: ${ve}`;
        else if (err?.response?.data?.message) errMsg = err.response.data.message;
        else if (err?.response?.data?.error) errMsg = `Validation error: ${err.response.data.error}`;
      } else { errMsg = err?.response?.data?.message || err?.response?.data?.detail || errMsg; }
      deps.setError(errMsg);
      deps.setLoading(false);
      toast.error(errMsg);
    }
  };

  return {
    loadAllTemplates,
    handleManualTemplateSelect,
    handleSeedTemplates,
    handleNavigateToLibrary,
    handlePreviewPdf,
    handlePreview,
    handleCreate,
  };
}
